import React, { useEffect, useState } from 'react';
import ConfirmModal from '../common/ConfirmModal';
import {
  connectGooglePlugin,
  disconnectGooglePlugin,
  getGooglePluginStatus,
  uploadToGoogleDrive,
} from '../../services/googlePluginService';

const GoogleDrivePanel = ({ onStatusChange } = {}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploads, setUploads] = useState([]);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const loadStatus = async () => {
    try {
      const data = await getGooglePluginStatus();
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    onStatusChange?.(Boolean(status?.driveConnected));
  }, [status, onStatusChange]);

  const handleConnect = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await connectGooglePlugin('drive');
      const data = await getGooglePluginStatus();
      setStatus(data);
      if (!data?.driveConnected) {
        throw new Error('Google connected, but Drive is not available yet. Click Connect again.');
      }
      setSuccess('Google Drive connected.');
    } catch (e) {
      setError(e.message || 'Could not connect Google Drive.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    setSuccess('');
    const next = [];
    try {
      for (const file of files) {
        const uploaded = await uploadToGoogleDrive(file);
        next.push(uploaded);
      }
      setUploads((prev) => [...next, ...prev]);
      setSuccess(next.length === 1 ? 'File uploaded. Share link is ready.' : `${next.length} files uploaded.`);
    } catch (e) {
      setError(e.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('Share link copied.');
    } catch {
      setError('Could not copy the link.');
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await disconnectGooglePlugin('drive');
      await loadStatus();
      setSuccess('Google Drive disconnected.');
    } catch (e) {
      setError(e.message || 'Could not disconnect.');
    } finally {
      setLoading(false);
      setConfirmDisconnect(false);
    }
  };

  const connected = Boolean(status?.driveConnected);
  const oauthAvailable = status?.oauthLoginAvailable !== false;

  return (
    <div>
      {error ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
          {success}
        </div>
      ) : null}

      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        Upload images and documents to Google Drive and share a link. Anyone with the link can view the file.
      </p>

      {connected ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Integrated</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {status.displayName || status.email || 'Google account'}
            {status.email && status.displayName ? ` · ${status.email}` : ''}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <label className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 cursor-pointer disabled:opacity-50">
              {uploading ? 'Uploading…' : 'Upload files'}
              <input
                type="file"
                multiple
                className="hidden"
                disabled={uploading}
                onChange={handleUpload}
              />
            </label>
            <button
              type="button"
              onClick={handleConnect}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            >
              Reconnect
            </button>
            <button
              type="button"
              onClick={() => setConfirmDisconnect(true)}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-rose-200 text-rose-700 dark:border-rose-800 dark:text-rose-300"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {oauthAvailable
              ? 'Sign in with Google to upload files and get share links.'
              : 'Google OAuth is not configured on the server. Set GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_CLIENT_SECRET.'}
          </p>
          <button
            type="button"
            onClick={handleConnect}
            disabled={loading || !oauthAvailable}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Connecting…' : 'Connect Google Drive'}
          </button>
        </div>
      )}

      {uploads.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {uploads.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2"
            >
              <a
                href={item.shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-700 dark:text-primary-300 truncate"
              >
                {item.name || 'File'}
              </a>
              <button
                type="button"
                onClick={() => copyLink(item.shareUrl)}
                className="flex-shrink-0 text-xs font-semibold text-gray-600 dark:text-gray-300"
              >
                Copy link
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <ConfirmModal
        isOpen={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect Google Drive"
        message="You will not be able to upload new files from WorkLoop until you connect again."
        confirmText="Disconnect"
        type="danger"
      />
    </div>
  );
};

export default GoogleDrivePanel;
